import { useEffect, useRef, useState } from "react";
import { chatService } from "../services";
import { pickEmotion } from "../constants/emotions";

const PROFILE_KEY = "askpurple_profile_v2";

// Generate a stable unique id for each chat message so React keeps DOM state
// consistent across re-renders (instead of using the array index).
let _msgCounter = 0;
const makeMsgId = () => `m-${Date.now()}-${++_msgCounter}`;

function loadProfile() {
  try {
    const saved = localStorage.getItem(PROFILE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    console.warn("AskPurple: failed to restore profile from localStorage", e);
    return null;
  }
}

function persistProfile(p) {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
  } catch (e) {
    console.warn("AskPurple: failed to persist profile to localStorage", e);
  }
}

function clearProfile() {
  try {
    localStorage.removeItem(PROFILE_KEY);
  } catch (e) {
    console.warn("AskPurple: failed to clear profile from localStorage", e);
  }
}

/**
 * Encapsulates AskPurple's chat state — profile, messages, send/receive,
 * idle expression cycling. Used by both the floating widget and the full-page
 * student app to keep behaviour identical.
 *
 * `welcomeForExisting(name)` / `welcomeForNew(name)` let each surface use its
 * own greeting tone.
 */
export default function useAskPurpleChat({
  welcomeForExisting,
  welcomeForNew,
  idleExpressions,
  idleIntervalMs = 6000,
}) {
  const [profile, setProfile] = useState(null);
  const [onboarded, setOnboarded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idleVariant, setIdleVariant] = useState(0);

  const scrollRef = useRef(null);

  // Restore onboarding profile on mount (persisted in localStorage)
  useEffect(() => {
    const saved = loadProfile();
    if (saved) {
      setProfile(saved);
      setOnboarded(true);
      const greeting = welcomeForExisting
        ? welcomeForExisting(saved.name)
        : "Welcome back!";
      setMessages([
        {
          id: makeMsgId(),
          role: "assistant",
          text: greeting,
          experts: [],
          emotion: "happy",
        },
      ]);
    }
  }, [welcomeForExisting]);

  // Idle mascot expression cycling when user isn't actively chatting
  useEffect(() => {
    if (!idleExpressions || idleExpressions.length === 0) return;
    const interval = setInterval(() => {
      setIdleVariant((v) => (v + 1) % idleExpressions.length);
    }, idleIntervalMs);
    return () => clearInterval(interval);
  }, [idleExpressions, idleIntervalMs]);

  // Auto-scroll message feed to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Determine current mascot state based on chat state:
  // loading -> typing
  // recent assistant message with emotion -> assistant's emotion
  // else cycle through idle expressions
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const mascotState = loading
    ? "typing"
    : lastAssistant?.emotion ||
      (idleExpressions && idleExpressions.length > 0
        ? idleExpressions[idleVariant]
        : "happy");

  const handleOnboardDone = (prof) => {
    setProfile(prof);
    setOnboarded(true);
    persistProfile(prof);
    const greeting = welcomeForNew
      ? welcomeForNew(prof.name)
      : "Hi! Ask me anything.";
    setMessages([
      {
        id: makeMsgId(),
        role: "assistant",
        text: greeting,
        experts: [],
        emotion: "happy",
      },
    ]);
  };

  const sendMessage = async (inputOrEvent) => {
    if (inputOrEvent && typeof inputOrEvent.preventDefault === "function") {
      inputOrEvent.preventDefault();
    }
    const text = (typeof inputOrEvent === "string" ? inputOrEvent : input).trim();
    if (!text || loading) return;

    setMessages((m) => [
      ...m,
      { id: makeMsgId(), role: "user", text, emotion: pickEmotion(text, "user") },
    ]);
    setInput("");
    setLoading(true);
    try {
      const data = await chatService.sendMessage({
        sessionId,
        message: text,
        contextVertical:
          profile?.need && profile.need !== "exploring" ? profile.need : "general",
        profile: profile || undefined,
      });
      setSessionId(data.session_id);
      setMessages((m) => [
        ...m,
        {
          id: makeMsgId(),
          role: "assistant",
          text: data.reply,
          experts: data.recommended_experts || [],
          emotion: pickEmotion(data.reply, "assistant"),
        },
      ]);
    } catch (err) {
      console.warn("AskPurple: chat request failed", err);
      setMessages((m) => [
        ...m,
        {
          id: makeMsgId(),
          role: "assistant",
          text: "I'm having trouble responding right now. Please try again or reach our team directly:\n\nDirect Support:\nWhatsApp: +91 91488 99369\nEmail: hello@crescentedustreams.in",
          experts: [],
          emotion: "confused",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const resetProfile = () => {
    clearProfile();
    setProfile(null);
    setOnboarded(false);
    setMessages([]);
    setSessionId(null);
  };

  const startFreshChat = (text) => {
    setMessages([{ id: makeMsgId(), role: "assistant", text, experts: [] }]);
  };

  return {
    profile,
    onboarded,
    messages,
    input,
    setInput,
    loading,
    mascotState,
    scrollRef,
    handleOnboardDone,
    sendMessage,
    resetProfile,
    startFreshChat,
  };
}
